import { useEffect, useState, useCallback } from "react";
import { useUser } from "../lib/UserContext";
import { supabase } from "../lib/supabase";
import {
  AreaChart, Area,
  BarChart, Bar,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  MessageSquare, Users, Calendar, TrendingUp,
  BarChart2, Bell, ToggleLeft, AlertCircle, RefreshCw,
} from "lucide-react";

interface KPIData {
  messages_24h: number;
  messages_10j: number;
  messages_mois: number;
  patients_uniques: number;
  reengages: number;
  jours_actifs: number;
  moyenne_session: number;
  rappels_mois: number;
  rappels_taux: number;
  bot_off: number;
  messages_24h_prev: number;
  messages_mois_prev: number;
  heures: { hour: number; total: number }[];
  jours_7: { jour: string; total: number }[];
}

// Fill the last 7 days with 0 if no data for that day
function fill7Days(data: { jour: string; total: number }[]) {
  const map = new Map(data.map((d) => [d.jour, d.total]));
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    const key = d.toISOString().slice(0, 10);
    const label = d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric" });
    return { jour: key, label, total: map.get(key) ?? 0 };
  });
}

function delta(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return Math.round(((current - previous) / previous) * 100);
}

// — UI Components —

function SkeletonCard() {
  return (
    <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-6 animate-pulse">
      <div className="flex items-center gap-2 mb-3">
        <div className="h-4 w-4 bg-gray-200 rounded" />
        <div className="h-3 bg-gray-200 rounded w-32" />
      </div>
      <div className="h-8 bg-gray-200 rounded w-20 mb-1" />
      <div className="h-3 bg-gray-200 rounded w-16" />
    </div>
  );
}

function DeltaBadge({ pct }: { pct: number | null }) {
  if (pct === null) return null;
  const up = pct >= 0;
  return (
    <span
      className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${
        up ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"
      }`}
    >
      {up ? "↑" : "↓"} {Math.abs(pct)}%
    </span>
  );
}

function KpiCard({
  icon: Icon,
  title,
  value,
  suffix = "",
  sub,
  deltaPct,
  iconColor = "text-primary",
}: {
  icon: React.ElementType;
  title: string;
  value: number;
  suffix?: string;
  sub?: string;
  deltaPct?: number | null;
  iconColor?: string;
}) {
  return (
    <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-gray-500">
          <Icon className={`w-4 h-4 ${iconColor}`} />
          <span className="text-sm">{title}</span>
        </div>
        {deltaPct !== undefined && <DeltaBadge pct={deltaPct ?? null} />}
      </div>
      <p className="text-3xl font-semibold text-primary">
        {value}
        {suffix}
      </p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 mt-6">
      {children}
    </h2>
  );
}

// — Main Dashboard —

export default function Dashboard() {
  useUser();
  const [kpi, setKpi] = useState<KPIData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchKPI = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) throw new Error("No session");

      const res = await fetch(`${import.meta.env.VITE_API_URL}/kpi`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      setKpi(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchKPI();
  }, [fetchKPI]);

  // Loading skeleton
  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="h-8 bg-gray-200 rounded w-48 mb-8 animate-pulse" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
          {Array.from({ length: 9 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
        <div className="h-72 bg-gray-100 rounded-xl animate-pulse" />
      </div>
    );
  }

  // Error state
  if (error || !kpi) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-col items-center justify-center gap-4 py-24 text-gray-500">
          <AlertCircle className="w-10 h-10 text-red-400" />
          <p className="text-sm">Failed to load the dashboard.</p>
          <button
            onClick={fetchKPI}
            className="flex items-center gap-2 text-sm px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  const reengagementRate =
    kpi.patients_uniques > 0
      ? Math.round((kpi.reengages / kpi.patients_uniques) * 100)
      : 0;

  const delta24h = delta(kpi.messages_24h, kpi.messages_24h_prev);
  const deltaMois = delta(kpi.messages_mois, kpi.messages_mois_prev);

  const days7 = fill7Days(kpi.jours_7 ?? []);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 font-sans text-gray-800">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-primary">Dashboard</h1>
        <button
          onClick={fetchKPI}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition"
          title="Refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>

      {/* — Activity — */}
      <SectionTitle>Activity</SectionTitle>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <KpiCard
          icon={MessageSquare}
          title="Messages (24h)"
          value={kpi.messages_24h}
          sub="vs previous 24h"
          deltaPct={delta24h}
        />
        <KpiCard
          icon={MessageSquare}
          title="Messages (10 days)"
          value={kpi.messages_10j}
          iconColor="text-blue-400"
        />
        <KpiCard
          icon={MessageSquare}
          title="Messages this month"
          value={kpi.messages_mois}
          sub="vs last month"
          deltaPct={deltaMois}
          iconColor="text-indigo-500"
        />
        <KpiCard
          icon={Calendar}
          title="Active days this month"
          value={kpi.jours_actifs}
          iconColor="text-orange-400"
        />
      </div>

      {/* — Patients — */}
      <SectionTitle>Patients</SectionTitle>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <KpiCard
          icon={Users}
          title="Unique patients"
          value={kpi.patients_uniques}
          sub="this month"
          iconColor="text-violet-500"
        />
        <KpiCard
          icon={TrendingUp}
          title="Re-engagement rate"
          value={reengagementRate}
          suffix="%"
          sub="returning patients this month"
          iconColor="text-emerald-500"
        />
        <KpiCard
          icon={BarChart2}
          title="Messages / session"
          value={Math.round(kpi.moyenne_session * 10) / 10}
          sub="average"
          iconColor="text-sky-500"
        />
      </div>

      {/* — Reminders & Automation — */}
      <SectionTitle>Reminders & Automation</SectionTitle>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <KpiCard
          icon={Bell}
          title="Reminders sent"
          value={kpi.rappels_mois}
          sub="this month"
          iconColor="text-amber-500"
        />
        <KpiCard
          icon={TrendingUp}
          title="Reminder response rate"
          value={Math.round(kpi.rappels_taux * 10) / 10}
          suffix="%"
          sub="patients who responded"
          iconColor="text-green-600"
        />
        <KpiCard
          icon={ToggleLeft}
          title="Bot disabled"
          value={kpi.bot_off}
          sub="conversations in manual mode"
          iconColor="text-rose-500"
        />
      </div>

      {/* — Charts — */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        {/* 7-day trend */}
        <div className="bg-white shadow-sm border rounded-xl p-6">
          <h2 className="text-sm font-semibold mb-4 text-slate-700">
            Trend — last 7 days
          </h2>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={days7} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.18} />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(val: number) => [val, "messages"]} />
              <Area
                type="monotone"
                dataKey="total"
                stroke="#6366f1"
                strokeWidth={2}
                fill="url(#areaGrad)"
                dot={{ r: 3, fill: "#6366f1" }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Peak hours */}
        <div className="bg-white shadow-sm border rounded-xl p-6">
          <h2 className="text-sm font-semibold mb-4 text-slate-700">
            Peak hours — this month
          </h2>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={kpi.heures} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="hour" tickFormatter={(h) => `${h}:00`} tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(val: number) => [val, "messages"]} />
              <Bar dataKey="total" fill="#3B82F6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
